/**
 * useOrgEntitlements — Phase-0 org-level entitlement flag hook.
 *
 * Separate from the user-tier `useEntitlements` hook (which targets the
 * subscription system). This hook backs the new Provider OS UI gating
 * (`provider_os` flag, default OFF for every org).
 *
 * Returns:
 *   - loading: boolean
 *   - flags: { provider_os: bool, hospital_scoreboard: bool, ... }
 *   - hasFlag(name): bool helper
 *
 * Backend: GET /api/entitlements/me — returns { org_id, flags }.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useOrgEntitlements() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) { setData(null); setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/entitlements/me`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data || {});
    } catch {
      setData({ flags: {} });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const hasFlag = useCallback((name) => {
    if (!data || !data.flags) return false;
    return Boolean(data.flags[name]);
  }, [data]);

  return { loading, flags: (data && data.flags) || {}, hasFlag, refresh };
}

export default useOrgEntitlements;
